import * as React from "react";
import { Select, Popover, Tag } from "antd";
const { Option, OptGroup } = Select;
import { observer } from "mobx-react";
import { contactsStore } from "Stores/ContactsStore";
import { generateUId } from "../utils/generateUId";
import { groupListStore } from "../../Groups/Stores/GroupsStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { SelectProps } from "antd/lib/select";
import { formatUsername } from "utils/formatUsername";

interface EmailSelectProps extends SelectProps<string[] | string> {
  value?: string | string[];
  onChange?: (val: string | string[]) => void;
  id?: string;
  className?: string;
  mode?: "multiple" | "tags";
  isGroupHidden?: boolean;
  placeholder?: string;
  field?: "to" | "cc" | "bcc";
  groupsData?: API_Group[];
  contactsData?: (API_Contact & API_User)[];
}

interface GroupUsersListProps {
  data: API_Group;
  onGroupOpen?: () => void;
  replaceWithEmails: (groupId: number, emails: string[]) => void;
}

class GroupUsersList extends React.Component<GroupUsersListProps> {
  handleCloseUser = (id: number) => {
    const { data } = this.props;
    const userUIds = data.groupMembers.filter((item) => item.id !== id).map((item) => generateUId(item));
    this.props.replaceWithEmails(data.id, userUIds);
  };

  get content() {
    const { data } = this.props;
    return data.groupMembers.map((item) => (
      <Tag key={item.id} closable={true} onClose={() => this.handleCloseUser(item.id)}>
        {formatUsername({ firstName: item.firstName, surname: item.surname })}
      </Tag>
    ));
  }

  handleClick = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    this.props.onGroupOpen();
    e.preventDefault();
    e.stopPropagation();
  };

  render() {
    const { data } = this.props;
    return (
      <div>
        <span className="email-select-group" onMouseDown={this.handleClick}>
          <Popover content={this.content} trigger="click" placement={"bottom"}>
            <span>
              <FontAwesomeIcon icon={"ellipsis-v-alt"} fixedWidth={true} />
            </span>
          </Popover>
        </span>
        {data.name}
      </div>
    );
  }
}

export const EmailSelect: React.FC<EmailSelectProps> = observer((props) => {
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [isOpen, setOpen] = React.useState(false);
  const handleReplaceGroupWithEmails = (groupId: number, userIds: string[]) => {
    const groupIndex = (props.value as string[]).findIndex((item) => item === `@group${groupId}`);
    if (groupIndex !== -1) {
      const copy = props.value.slice();
      if (Array.isArray(copy)) {
        (copy as string[]).splice(groupIndex, 1, ...userIds);
      }
      props.onChange(copy);
    }
  };

  const formatContactName = (data: API_Contact) => {
    let name = data.name;
    if (data.email) {
      name += ` (${data.email})`;
    }
    return name;
  };

  const { mode, showSearch, isGroupHidden, placeholder, className, contactsData, groupsData, ...rest } = props;
  const cData = contactsData?.length ? contactsData : contactsStore.allData.filter((item) => item.active);
  const contacts = cData.map((data) => {
    const uid = generateUId(data);

    return (
      <Option
        key={`${uid}`}
        value={uid}
        disabled={!data.active}
        title={`${data.email} (${formatUsername({ firstName: data.firstName, surname: data.surname })})`}
      >
        {Object.prototype.hasOwnProperty.call(data, "contactType")
          ? formatContactName(data)
          : formatUsername({ firstName: data.firstName, surname: data.surname })}
      </Option>
    );
  });

  const gData = groupsData?.length ? groupsData : groupListStore.forMailAndPlot;
  const groups = gData.map((group) => {
    return (
      <Option key={`${group.id}`} title={group.name} value={`@group${group.id}`}>
        <GroupUsersList
          onGroupOpen={() => setOpen(false)}
          replaceWithEmails={handleReplaceGroupWithEmails}
          data={group}
        />
      </Option>
    );
  });

  /**
   * Since we can have a multiple modals open at the same time (e.g. email + collection folder)
   * we have to pick a last modal (.ant-modal-container) as a container reference
   */
  const getContainer = () => {
    const arr = Array.from(document.querySelectorAll(".ant-modal-container"));
    return arr.reverse()[0] as HTMLElement;
  };

  return (
    <Select
      // NOTE: getPopupContainer is used to stick dropdown to modal while scrolling.
      // Consider this when using component outside modal window
      getPopupContainer={getContainer}
      // NOTE: In order to close the dropdown of a select when the group popover is open.
      // It does not work automatically, since the select is always in focus.
      open={isOpen}
      onOpenChange={(v) => (isOpen ? setOpen(false) : setOpen(v))}
      mode={mode}
      style={{ width: "100%", marginRight: "40px", maxHeight: 120, minHeight: 44, overflow: "auto" }}
      tokenSeparators={[",", ";"]}
      placeholder={placeholder}
      showSearch={{ optionFilterProp: "title", ...(typeof showSearch === "object" ? showSearch : {}) }}
      className={className || "email-select"}
      {...rest}
    >
      <OptGroup label={t(`Contacts`)}>{contacts}</OptGroup>
      {!isGroupHidden && <OptGroup label={t(`Groups`)}>{groups}</OptGroup>}
    </Select>
  );
});
